import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DollarSign, Calculator, Edit2, Trash2, Plus, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API_URL } from '../config';

const API = API_URL;

export const EventFinances = ({ evento, onUpdate }) => {
  const [remuneraciones, setRemuneraciones] = useState(evento.remuneraciones || []);
  const [cobrado, setCobrado] = useState(evento.cobrado || false);
  const [calculating, setCalculating] = useState(false);
  const [editingRem, setEditingRem] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);

  const calculateRemuneraciones = async () => {
    try {
      setCalculating(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/financial/eventos/${evento.id}/calculate-remuneraciones`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setRemuneraciones(response.data.remuneraciones);
      toast.success(`Remuneraciones calculadas: ${response.data.total}€ total`);
      onUpdate?.();
    } catch (error) {
      console.error('Error calculating remuneraciones:', error);
      toast.error('Error al calcular remuneraciones');
    } finally {
      setCalculating(false);
    }
  };

  const saveRemuneraciones = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/financial/eventos/${evento.id}/remuneraciones`,
        remuneraciones,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Remuneraciones guardadas');
      onUpdate?.();
    } catch (error) {
      console.error('Error saving remuneraciones:', error);
      toast.error('Error al guardar remuneraciones');
    }
  };

  const toggleCobrado = async () => {
    try {
      const token = localStorage.getItem('token');
      const newCobrado = !cobrado;
      
      await axios.patch(
        `${API}/financial/eventos/${evento.id}/mark-cobrado`,
        {
          cobrado: newCobrado,
          fecha_cobro: newCobrado ? new Date().toISOString() : null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setCobrado(newCobrado);
      toast.success(newCobrado ? 'Evento marcado como cobrado' : 'Marcado como no cobrado');
      onUpdate?.();
    } catch (error) {
      console.error('Error toggling cobrado:', error);
      toast.error('Error al actualizar estado de cobro');
    }
  };

  const addRemuneration = () => {
    setEditingRem({
      id: Date.now().toString(),
      nombre: '',
      rol: '',
      servicio: '',
      monto: 0,
      pagado: false
    });
    setShowEditDialog(true);
  };

  const editRemuneration = (rem) => {
    setEditingRem({...rem});
    setShowEditDialog(true);
  };

  const saveEditedRemuneration = () => {
    if (editingRem.id && remuneraciones.find(r => r.id === editingRem.id)) {
      // Update existing
      setRemuneraciones(remuneraciones.map(r => r.id === editingRem.id ? editingRem : r));
    } else {
      // Add new
      setRemuneraciones([...remuneraciones, editingRem]);
    }
    setShowEditDialog(false);
    setEditingRem(null);
  };

  const deleteRemuneration = (id) => {
    if (window.confirm('¿Eliminar esta remuneración?')) {
      setRemuneraciones(remuneraciones.filter(r => r.id !== id));
    }
  };

  const totalRemuneraciones = remuneraciones.reduce((sum, r) => sum + (r.monto || 0), 0);
  const cacheAcordado = evento.cache_acordado || 0;
  const margen = cacheAcordado - totalRemuneraciones;

  return (
    <div className="space-y-4">
      {/* Header con Caché y Estado de Cobro */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-emerald-900/30 to-emerald-800/20 border-emerald-700/50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-300 text-sm">Caché Acordado</p>
                <p className="text-3xl font-bold text-white">{cacheAcordado}€</p>
              </div>
              <DollarSign className="w-10 h-10 text-emerald-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-900/30 to-purple-800/20 border-purple-700/50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-300 text-sm">Total Remuneraciones</p>
                <p className="text-3xl font-bold text-white">{totalRemuneraciones}€</p>
              </div>
              <DollarSign className="w-10 h-10 text-purple-400" />
            </div>
          </CardContent>
        </Card>

        <Card className={`bg-gradient-to-br ${margen >= 0 ? 'from-cyan-900/30 to-cyan-800/20 border-cyan-700/50' : 'from-red-900/30 to-red-800/20 border-red-700/50'}`}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className={`${margen >= 0 ? 'text-cyan-300' : 'text-red-300'} text-sm`}>
                  Margen {margen < 0 && '(⚠️ Negativo)'}
                </p>
                <p className="text-3xl font-bold text-white">{margen}€</p>
              </div>
              <DollarSign className={`w-10 h-10 ${margen >= 0 ? 'text-cyan-400' : 'text-red-400'}`} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Estado de Cobro */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Checkbox
                checked={cobrado}
                onCheckedChange={toggleCobrado}
                className="data-[state=checked]:bg-emerald-600"
              />
              <div>
                <Label className="text-white text-lg cursor-pointer" onClick={toggleCobrado}>
                  Evento cobrado
                </Label>
                <p className="text-slate-400 text-sm">
                  Marca cuando hayas recibido el pago del cliente
                </p>
              </div>
            </div>
            {cobrado && (
              <Badge className="bg-emerald-600 text-white">
                <CheckCircle className="w-3 h-3 mr-1" />
                Cobrado
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Remuneraciones */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-purple-400" />
              Remuneraciones DJ/Asistentes
            </CardTitle>
            <div className="flex gap-2">
              <Button
                onClick={calculateRemuneraciones}
                disabled={calculating}
                className="bg-cyan-600 hover:bg-cyan-700"
                size="sm"
              >
                <Calculator className="w-4 h-4 mr-2" />
                {calculating ? 'Calculando...' : 'Calcular Auto'}
              </Button>
              <Button
                onClick={addRemuneration}
                className="bg-emerald-600 hover:bg-emerald-700"
                size="sm"
              >
                <Plus className="w-4 h-4 mr-2" />
                Añadir
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {remuneraciones.length === 0 ? (
            <div className="text-center py-8">
              <DollarSign className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400 mb-2">No hay remuneraciones configuradas</p>
              <p className="text-slate-500 text-sm">
                Haz click en "Calcular Auto" para generar automáticamente desde los servicios
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {remuneraciones.map((rem) => (
                <div
                  key={rem.id}
                  className="bg-slate-700/50 p-4 rounded-lg border border-slate-600"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="text-white font-medium">{rem.nombre}</h4>
                        <Badge variant="outline" className="text-xs">
                          {rem.rol}
                        </Badge>
                        {rem.pagado && (
                          <Badge className="bg-green-600 text-white text-xs">
                            Pagado
                          </Badge>
                        )}
                      </div>
                      <p className="text-slate-400 text-sm">{rem.servicio}</p>
                      {rem.fecha_pago && (
                        <p className="text-slate-500 text-xs mt-1">
                          Pagado: {new Date(rem.fecha_pago).toLocaleDateString('es-ES')}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="text-emerald-400 font-semibold text-lg">{rem.monto}€</p>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          onClick={() => editRemuneration(rem)}
                          size="sm"
                          variant="outline"
                          className="h-8 w-8 p-0"
                        >
                          <Edit2 className="w-3 h-3" />
                        </Button>
                        <Button
                          onClick={() => deleteRemuneration(rem.id)}
                          size="sm"
                          variant="outline"
                          className="h-8 w-8 p-0 text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Total */}
              <div className="pt-4 border-t border-slate-600">
                <div className="flex justify-between items-center">
                  <span className="text-slate-300 font-medium">Total a Pagar:</span>
                  <span className="text-2xl font-bold text-emerald-400">{totalRemuneraciones}€</span>
                </div>
              </div>
              
              {/* Save Button */}
              <Button
                onClick={saveRemuneraciones}
                className="w-full bg-purple-600 hover:bg-purple-700 mt-4"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Guardar Remuneraciones
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Warning if margen is negative */}
      {margen < 0 && (
        <Card className="bg-red-900/20 border-red-700/50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-red-400 flex-shrink-0" />
              <div>
                <p className="text-red-300 font-medium">Alerta: Margen Negativo</p>
                <p className="text-red-400/80 text-sm mt-1">
                  Las remuneraciones ({totalRemuneraciones}€) superan el caché acordado ({cacheAcordado}€).
                  Verifica los montos para evitar pérdidas.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Edit Remuneration Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>
              {editingRem && remuneraciones.find(r => r.id === editingRem.id) ? 'Editar' : 'Nueva'} Remuneración
            </DialogTitle>
          </DialogHeader>
          {editingRem && (
            <div className="space-y-4">
              <div>
                <Label>Nombre DJ/Asistente</Label>
                <Input
                  value={editingRem.nombre}
                  onChange={(e) => setEditingRem({...editingRem, nombre: e.target.value})}
                  placeholder="Ej: Juan García"
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <div>
                <Label>Rol</Label>
                <Input
                  value={editingRem.rol}
                  onChange={(e) => setEditingRem({...editingRem, rol: e.target.value})}
                  placeholder="Ej: DJ Principal, Asistente"
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <div>
                <Label>Servicio</Label>
                <Input
                  value={editingRem.servicio}
                  onChange={(e) => setEditingRem({...editingRem, servicio: e.target.value})}
                  placeholder="Ej: DJ 4 horas"
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <div>
                <Label>Monto (€)</Label>
                <Input
                  type="number"
                  value={editingRem.monto}
                  onChange={(e) => setEditingRem({...editingRem, monto: parseFloat(e.target.value) || 0})}
                  placeholder="Ej: 400"
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <Button
                onClick={saveEditedRemuneration}
                className="w-full bg-emerald-600 hover:bg-emerald-700"
              >
                Guardar
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EventFinances;
